import { useForm } from 'react-hook-form';
import {
  StyledFormField,
  StyledGenericForm,
  StyledGenericFieldset,
  StyledFormGroup,
  StyledInput,
} from '@/components/uielements/form-fields/styles';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import { DEFAULT_PASSWORD_LOGIN_EXTRA_PAYLOAD } from '@/constants/authentication';
import { type IUserLogin } from '@/types/authentication';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import Label from '@/components/uielements/label';
import { Render } from '@/components/uielements/render';
import { ConnectionErrorState } from '@/components/connectionError';

interface Props {
  loading: boolean;
  onLogin: (values?: IUserLogin, referrer?: string | boolean | null) => void;
  referrer?: string | boolean | null;
  loadingCaptcha?: boolean;
  /** TEAM-14320: the login call got no readable response — show the retryable banner. */
  connectionError?: boolean;
  /**
   * TEAM-14320: true from the moment a submit starts (captcha fetch included) until the
   * flow resolves. Redux `loading` and `loadingCaptcha` both leave the captcha round-trip
   * uncovered, so the retry button — and, since TEAM-14519, the submit button — would
   * otherwise stay pressable through it.
   */
  submitting?: boolean;
}

const LoginForm: React.FC<Props> = ({
  loading,
  onLogin,
  loadingCaptcha,
  connectionError,
  submitting,
}) => {
  const { handleSubmit, register } = useForm();
  const extraPayload = DEFAULT_PASSWORD_LOGIN_EXTRA_PAYLOAD;
  // TEAM-14519: busy for the whole attempt, captcha fetch included. `loadingCaptcha` alone
  // doesn't lock the button: a press before the script loads gets the captcha failure message.
  const isBusy = loading || submitting;
  const accessor = !isBusy ? 'login.cta' : 'login.cta_loading';

  const onSubmit = (data: IUserLogin) => {
    onLogin(Object.assign({ identifier: data.identifier, secret: data.secret }, extraPayload));
  };

  return (
    <StyledGenericForm
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit(onSubmit)(e);
      }}
    >
      {/*
        TEAM-14320: react-hook-form never resets this form, so the diner's credentials are
        still in the inputs — re-running handleSubmit re-submits the identical login and
        refetches the captcha, matching sign-up's retry.
      */}
      <Render condition={Boolean(connectionError)}>
        <ConnectionErrorState
          variant="banner"
          data-testid="login-connection-error"
          retryTestId="login-connection-retry"
          isRetrying={Boolean(loading || loadingCaptcha || submitting)}
          onRetry={() => {
            void handleSubmit(onSubmit)();
          }}
        />
      </Render>

      <StyledGenericFieldset>
        <legend>Login form</legend>
        <StyledFormGroup style={{ display: 'flex', flexDirection: 'column', gap: '0rem' }}>
          <StyledFormField key={`login-form-field-identifier`} width="100%" $autoHeight>
            <Label visuallyHidden={false} htmlFor="identifier">
              Email
            </Label>
            <StyledInput
              key="identifier"
              type="email"
              placeholder="Enter Email"
              autoComplete="email"
              {...register('identifier')}
              autoFocus={false}
              style={{ marginBottom: '16px' }}
            />
          </StyledFormField>

          <StyledFormField key={`login-form-field-password`} width="100%" $autoHeight>
            <Label visuallyHidden={false} htmlFor="identifier">
              Password
            </Label>
            <StyledInput
              key="secret"
              type="password"
              placeholder="Enter Password"
              autoComplete="current-password"
              {...register('secret')}
              autoFocus={false}
              style={{ marginBottom: '16px' }}
            />
          </StyledFormField>
        </StyledFormGroup>
      </StyledGenericFieldset>

      {/* Submit Button */}
      <StyledPrimaryButton
        data-testid="signin-button"
        $submitting={isBusy}
        disabled={isBusy}
        type="submit"
        style={{ width: '100%' }}
        $buttonTheme="primary"
      >
        <Render
          condition={!!loadingCaptcha}
          whenTrue={'Loading'}
          whenFalse={<StringAccessor accessor={accessor} />}
        />
      </StyledPrimaryButton>
    </StyledGenericForm>
  );
};

export default LoginForm;
